import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  getQuizConfigs,
  updateQuizConfig,
  QuizConfig,
  deleteQuizConfig
} from '../services/api';
import { GlassCard } from './ui/GlassCard';
import { GlassButton } from './ui/GlassButton';
import { GlassBadge } from './ui/GlassBadge';
import { ModuleHeader } from './ui/ModuleHeader';
import {
  Plus, Eye, EyeOff, Trash2, ClipboardList,
  Edit2, Save, X, ChevronUp, ChevronDown,
} from 'lucide-react';
import './QuizzesList.css';

interface QuizzesListProps {
  projectId?: string;
}

// ── Input helpers ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
  borderRadius: 'var(--radius-md)', padding: '7px 10px',
  color: 'var(--text)', fontSize: '0.875rem', outline: 'none',
};
const textareaStyle: React.CSSProperties = {
  ...inputStyle, resize: 'vertical', minHeight: '64px',
};
const numInputStyle: React.CSSProperties = {
  ...inputStyle, width: '80px',
};

const QuizzesList: React.FC<QuizzesListProps> = ({ projectId }) => {
  const ctx = useOutletContext<{ selectedProjectId?: string }>();
  const effectiveProjectId = projectId || ctx?.selectedProjectId;
  const navigate = useNavigate();

  const [quizzes, setQuizzes] = useState<QuizConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedQuizId, setExpandedQuizId] = useState<string | null>(null);

  // Edit state
  const [editingQuizId, setEditingQuizId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<QuizConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    loadQuizzes();
  }, [effectiveProjectId]);

  const loadQuizzes = async () => {
    setIsLoading(true);
    try {
      const data = await getQuizConfigs(effectiveProjectId);
      setQuizzes(data);
    } catch (err) {
      console.error('Failed to load quizzes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (quizId: string) => {
    if (!window.confirm('Delete this quiz? This action cannot be undone.')) return;
    try {
      await deleteQuizConfig(quizId);
      loadQuizzes();
    } catch (err) {
      alert('Failed to delete quiz');
    }
  };

  const toggleExpand = (quizId: string) => {
    if (editingQuizId === quizId) return; // don't collapse while editing
    setExpandedQuizId(prev => (prev === quizId ? null : quizId));
  };

  // ── Edit helpers ─────────────────────────────────────────────────────────

  const startEdit = (quiz: QuizConfig) => {
    setEditDraft(JSON.parse(JSON.stringify(quiz))); // deep clone
    setEditingQuizId(quiz.quiz_id);
    setExpandedQuizId(quiz.quiz_id);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingQuizId(null);
    setEditDraft(null);
    setSaveError(null);
  };

  const saveEdit = async () => {
    if (!editDraft) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateQuizConfig(editDraft.quiz_id, {
        name: editDraft.name,
        description: editDraft.description,
        questions: editDraft.questions,
        scoring_logic: editDraft.scoring_logic,
        show_progress: editDraft.show_progress,
        allow_back: editDraft.allow_back,
        require_all_answers: editDraft.require_all_answers,
        lead_magnet_enabled: editDraft.lead_magnet_enabled,
        lead_magnet_url: editDraft.lead_magnet_url,
        lead_magnet_label: editDraft.lead_magnet_label,
      });
      setEditingQuizId(null);
      setEditDraft(null);
      await loadQuizzes();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || 'Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  // Draft field setters
  const setDraftField = (field: keyof QuizConfig, value: any) => {
    setEditDraft(prev => prev ? { ...prev, [field]: value } : prev);
  };

  // Question operations
  const updateQuestionText = (qIdx: number, text: string) => {
    setEditDraft(prev => {
      if (!prev) return prev;
      const questions = prev.questions.map((q, i) =>
        i === qIdx ? { ...q, question: text } : q
      );
      return { ...prev, questions };
    });
  };

  const moveQuestion = (qIdx: number, dir: -1 | 1) => {
    setEditDraft(prev => {
      if (!prev) return prev;
      const q = [...prev.questions];
      const target = qIdx + dir;
      if (target < 0 || target >= q.length) return prev;
      [q[qIdx], q[target]] = [q[target], q[qIdx]];
      return { ...prev, questions: q };
    });
  };

  const addQuestion = () => {
    setEditDraft(prev => {
      if (!prev) return prev;
      const newId = (prev.questions.reduce((m, q) => Math.max(m, q.id || 0), 0)) + 1;
      const newQ = { id: newId, question: '', options: [{ value: 'a', label: '', score: 0 }] };
      return { ...prev, questions: [...prev.questions, newQ] };
    });
  };

  const removeQuestion = (qIdx: number) => {
    setEditDraft(prev => {
      if (!prev) return prev;
      return { ...prev, questions: prev.questions.filter((_, i) => i !== qIdx) };
    });
  };

  // Option operations
  const updateOption = (qIdx: number, optIdx: number, field: 'label' | 'score' | 'value', value: any) => {
    setEditDraft(prev => {
      if (!prev) return prev;
      const questions = prev.questions.map((q, i) => {
        if (i !== qIdx) return q;
        const options = q.options.map((opt: any, j: number) =>
          j === optIdx ? { ...opt, [field]: field === 'score' ? Number(value) : value } : opt
        );
        return { ...q, options };
      });
      return { ...prev, questions };
    });
  };

  const addOption = (qIdx: number) => {
    setEditDraft(prev => {
      if (!prev) return prev;
      const questions = prev.questions.map((q, i) => {
        if (i !== qIdx) return q;
        const letters = 'abcdefghijklmnopqrstuvwxyz';
        const val = letters[q.options.length] || String(q.options.length + 1);
        return { ...q, options: [...q.options, { value: val, label: '', score: 0 }] };
      });
      return { ...prev, questions };
    });
  };

  const removeOption = (qIdx: number, optIdx: number) => {
    setEditDraft(prev => {
      if (!prev) return prev;
      const questions = prev.questions.map((q, i) => {
        if (i !== qIdx) return q;
        return { ...q, options: q.options.filter((_: any, j: number) => j !== optIdx) };
      });
      return { ...prev, questions };
    });
  };

  // Scoring range operations
  const updateScoringRange = (rIdx: number, field: string, value: any) => {
    setEditDraft(prev => {
      if (!prev?.scoring_logic?.ranges) return prev;
      const ranges = prev.scoring_logic.ranges.map((r: any, i: number) =>
        i === rIdx ? { ...r, [field]: ['min', 'max'].includes(field) ? Number(value) : value } : r
      );
      return { ...prev, scoring_logic: { ...prev.scoring_logic, ranges } };
    });
  };

  const addScoringRange = () => {
    setEditDraft(prev => {
      if (!prev) return prev;
      const ranges = prev.scoring_logic?.ranges || [];
      const newRange = { min: 0, max: 0, level: '', message: '' };
      return { ...prev, scoring_logic: { ...(prev.scoring_logic || {}), ranges: [...ranges, newRange] } };
    });
  };

  const removeScoringRange = (rIdx: number) => {
    setEditDraft(prev => {
      if (!prev?.scoring_logic?.ranges) return prev;
      const ranges = prev.scoring_logic.ranges.filter((_: any, i: number) => i !== rIdx);
      return { ...prev, scoring_logic: { ...prev.scoring_logic, ranges } };
    });
  };

  // ── Misc ──────────────────────────────────────────────────────────────────

  const getQuizTypeBadgeVariant = (quizType: string): 'success' | 'warning' | 'info' | 'default' => {
    switch (quizType) {
      case 'assessment': return 'success';
      case 'personality': return 'warning';
      case 'knowledge': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="quizzes-list">
        <div className="loading">Loading quizzes...</div>
      </div>
    );
  }

  return (
    <div className="quizzes-list">
      <ModuleHeader
        title="Quizzes & Assessments"
        breadcrumb={['Home', 'Quizzes']}
        actions={[
          <GlassButton
            key="create"
            variant="primary"
            onClick={() => navigate('/quizzes/create')}
            icon={<Plus size={18} />}
          >
            Create Quiz
          </GlassButton>
        ]}
      />

      {!effectiveProjectId ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          <ClipboardList size={64} style={{ opacity: 0.4, marginBottom: '16px' }} />
          <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>Select a project</h3>
          <p>Choose a project from the sidebar to view its quizzes</p>
        </div>
      ) : quizzes.length === 0 ? (
        <GlassCard variant="default" padding="lg" className="empty-state-card">
          <div className="empty-state">
            <h2>No quizzes yet</h2>
            <p>Create your first interactive quiz to engage visitors and capture leads.</p>
            <GlassButton
              variant="primary"
              onClick={() => navigate('/quizzes/create')}
              icon={<Plus size={18} />}
            >
              Create Quiz
            </GlassButton>
          </div>
        </GlassCard>
      ) : (
        <div className="quizzes-grid">
          {quizzes.map(quiz => {
            const isExpanded = expandedQuizId === quiz.quiz_id;
            const isEditing = editingQuizId === quiz.quiz_id;
            const draft = isEditing ? editDraft! : null;

            return (
              <GlassCard
                key={quiz.quiz_id}
                variant={isExpanded ? 'strong' : 'hover'}
                padding="md"
                className={`quiz-card ${isExpanded ? 'expanded' : ''}`}
              >
                {/* ── Card header (always visible) ── */}
                <div className="card-main">
                  <div className="card-header">
                    <div className="title-area">
                      <h3>{quiz.name}</h3>
                      <GlassBadge variant={getQuizTypeBadgeVariant(quiz.quiz_type)} size="sm">
                        {quiz.quiz_type}
                      </GlassBadge>
                    </div>
                    <div className="card-meta">
                      <span>Created: {formatDate(quiz.created_at)}</span>
                    </div>
                  </div>

                  <div className="card-description">
                    <p>{quiz.description || 'No description provided.'}</p>
                  </div>

                  <div className="card-stats">
                    <div className="stat">
                      <span className="stat-label">Questions</span>
                      <span className="stat-value">{quiz.questions?.length || 0}</span>
                    </div>
                    {quiz.scoring_logic?.ranges && (
                      <div className="stat">
                        <span className="stat-label">Score Ranges</span>
                        <span className="stat-value">{quiz.scoring_logic.ranges.length}</span>
                      </div>
                    )}
                  </div>

                  <div className="card-actions">
                    {!isEditing && (
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpand(quiz.quiz_id)}
                        icon={isExpanded ? <EyeOff size={16} /> : <Eye size={16} />}
                      >
                        {isExpanded ? 'Hide Details' : 'View Details'}
                      </GlassButton>
                    )}

                    {!isEditing ? (
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={() => startEdit(quiz)}
                        icon={<Edit2 size={16} />}
                      >
                        Edit
                      </GlassButton>
                    ) : (
                      <>
                        <GlassButton
                          variant="primary"
                          size="sm"
                          onClick={saveEdit}
                          disabled={saving}
                          icon={<Save size={16} />}
                        >
                          {saving ? 'Saving…' : 'Save Changes'}
                        </GlassButton>
                        <GlassButton
                          variant="secondary"
                          size="sm"
                          onClick={cancelEdit}
                          disabled={saving}
                          icon={<X size={16} />}
                        >
                          Cancel
                        </GlassButton>
                      </>
                    )}

                    {!isEditing && (
                      <GlassButton
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(quiz.quiz_id)}
                        icon={<Trash2 size={16} />}
                      >
                        Delete
                      </GlassButton>
                    )}
                  </div>
                </div>

                {/* ── Expanded: read-only detail view ── */}
                {isExpanded && !isEditing && (
                  <div className="card-details" style={{
                    marginTop: 'var(--space-4)',
                    paddingTop: 'var(--space-4)',
                    borderTop: '1px solid var(--divider)'
                  }}>
                    <h4>Questions</h4>
                    <div className="questions-list">
                      {quiz.questions?.map((q, idx) => (
                        <GlassCard key={idx} variant="default" padding="sm" className="detail-question">
                          <strong>Q{idx + 1}: {q.question}</strong>
                          <ul>
                            {q.options?.map((opt: any, optIdx: number) => (
                              <li key={optIdx}>
                                {opt.label} <small>(Score: {opt.score})</small>
                              </li>
                            ))}
                          </ul>
                        </GlassCard>
                      ))}
                    </div>

                    {quiz.scoring_logic?.ranges && (
                      <div className="scoring-details" style={{ marginTop: 'var(--space-4)' }}>
                        <h4>Scoring Logic</h4>
                        <div className="ranges">
                          {quiz.scoring_logic.ranges.map((range: any, idx: number) => (
                            <GlassCard key={idx} variant="default" padding="sm" className="range-item">
                              <span className="range-level">{range.level}</span>
                              <span className="range-scores">{range.min}–{range.max} points</span>
                              <p className="range-msg">{range.message}</p>
                            </GlassCard>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Expanded: edit form ── */}
                {isExpanded && isEditing && draft && (
                  <div className="card-details quiz-editor">
                    {saveError && (
                      <div className="quiz-editor-error">{saveError}</div>
                    )}

                    {/* Name & Description */}
                    <div className="quiz-editor-section">
                      <h4>Basic Info</h4>
                      <div className="quiz-editor-field">
                        <label>Name</label>
                        <input
                          style={inputStyle}
                          value={draft.name}
                          onChange={e => setDraftField('name', e.target.value)}
                        />
                      </div>
                      <div className="quiz-editor-field">
                        <label>Description</label>
                        <textarea
                          style={textareaStyle}
                          value={draft.description || ''}
                          onChange={e => setDraftField('description', e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Questions */}
                    <div className="quiz-editor-section">
                      <div className="quiz-editor-section-header">
                        <h4>Questions ({draft.questions.length})</h4>
                        <GlassButton variant="secondary" size="sm" onClick={addQuestion} icon={<Plus size={14} />}>
                          Add Question
                        </GlassButton>
                      </div>

                      {draft.questions.map((q, qIdx) => (
                        <GlassCard key={qIdx} variant="default" padding="sm" className="quiz-editor-question">
                          <div className="quiz-editor-question-header">
                            <span className="quiz-editor-q-num">Q{qIdx + 1}</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                className="quiz-editor-icon-btn"
                                title="Move up"
                                disabled={qIdx === 0}
                                onClick={() => moveQuestion(qIdx, -1)}
                              >
                                <ChevronUp size={14} />
                              </button>
                              <button
                                className="quiz-editor-icon-btn"
                                title="Move down"
                                disabled={qIdx === draft.questions.length - 1}
                                onClick={() => moveQuestion(qIdx, 1)}
                              >
                                <ChevronDown size={14} />
                              </button>
                              <button
                                className="quiz-editor-icon-btn danger"
                                title="Delete question"
                                onClick={() => removeQuestion(qIdx)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          <div className="quiz-editor-field">
                            <label>Question text</label>
                            <textarea
                              style={{ ...textareaStyle, minHeight: '48px' }}
                              value={q.question}
                              onChange={e => updateQuestionText(qIdx, e.target.value)}
                            />
                          </div>

                          <div className="quiz-editor-options">
                            <div className="quiz-editor-options-header">
                              <label>Options</label>
                              <button
                                className="quiz-editor-add-opt-btn"
                                onClick={() => addOption(qIdx)}
                              >
                                <Plus size={12} /> Add option
                              </button>
                            </div>
                            {q.options?.map((opt: any, optIdx: number) => (
                              <div key={optIdx} className="quiz-editor-option-row">
                                <input
                                  style={{ ...inputStyle, flex: 1 }}
                                  placeholder="Option label"
                                  value={opt.label}
                                  onChange={e => updateOption(qIdx, optIdx, 'label', e.target.value)}
                                />
                                <div className="quiz-editor-score-wrap">
                                  <span className="quiz-editor-score-label">Score</span>
                                  <input
                                    type="number"
                                    style={numInputStyle}
                                    value={opt.score}
                                    onChange={e => updateOption(qIdx, optIdx, 'score', e.target.value)}
                                  />
                                </div>
                                <button
                                  className="quiz-editor-icon-btn danger"
                                  title="Remove option"
                                  disabled={q.options.length <= 1}
                                  onClick={() => removeOption(qIdx, optIdx)}
                                >
                                  <X size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </GlassCard>
                      ))}
                    </div>

                    {/* Scoring ranges */}
                    {(draft.scoring_logic?.ranges !== undefined) && (
                      <div className="quiz-editor-section">
                        <div className="quiz-editor-section-header">
                          <h4>Scoring Ranges</h4>
                          <GlassButton variant="secondary" size="sm" onClick={addScoringRange} icon={<Plus size={14} />}>
                            Add Range
                          </GlassButton>
                        </div>
                        {(draft.scoring_logic?.ranges || []).map((range: any, rIdx: number) => (
                          <GlassCard key={rIdx} variant="default" padding="sm" className="quiz-editor-range">
                            <div className="quiz-editor-range-row">
                              <div className="quiz-editor-field" style={{ flex: '0 0 80px' }}>
                                <label>Min</label>
                                <input type="number" style={numInputStyle} value={range.min}
                                  onChange={e => updateScoringRange(rIdx, 'min', e.target.value)} />
                              </div>
                              <div className="quiz-editor-field" style={{ flex: '0 0 80px' }}>
                                <label>Max</label>
                                <input type="number" style={numInputStyle} value={range.max}
                                  onChange={e => updateScoringRange(rIdx, 'max', e.target.value)} />
                              </div>
                              <div className="quiz-editor-field" style={{ flex: 1 }}>
                                <label>Level label</label>
                                <input style={inputStyle} value={range.level}
                                  placeholder="e.g. low, moderate, high"
                                  onChange={e => updateScoringRange(rIdx, 'level', e.target.value)} />
                              </div>
                              <button
                                className="quiz-editor-icon-btn danger"
                                style={{ alignSelf: 'flex-end', marginBottom: '2px' }}
                                onClick={() => removeScoringRange(rIdx)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="quiz-editor-field" style={{ marginTop: '8px' }}>
                              <label>Result message</label>
                              <textarea style={{ ...textareaStyle, minHeight: '56px' }}
                                value={range.message}
                                onChange={e => updateScoringRange(rIdx, 'message', e.target.value)} />
                            </div>
                          </GlassCard>
                        ))}
                      </div>
                    )}

                    {/* Settings */}
                    <div className="quiz-editor-section">
                      <h4>Settings</h4>
                      <div className="quiz-editor-settings-row">
                        {([
                          { key: 'show_progress', label: 'Show progress bar' },
                          { key: 'allow_back', label: 'Allow going back' },
                          { key: 'require_all_answers', label: 'Require all answers' },
                        ] as { key: keyof QuizConfig; label: string }[]).map(({ key, label }) => (
                          <label key={key} className="quiz-editor-toggle">
                            <input
                              type="checkbox"
                              checked={!!(draft as any)[key]}
                              onChange={e => setDraftField(key, e.target.checked)}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Lead magnet */}
                    <div className="quiz-editor-section">
                      <h4>Lead Magnet</h4>
                      <label className="quiz-editor-toggle" style={{ marginBottom: '12px' }}>
                        <input
                          type="checkbox"
                          checked={!!draft.lead_magnet_enabled}
                          onChange={e => setDraftField('lead_magnet_enabled', e.target.checked)}
                        />
                        <span>Enable lead magnet</span>
                      </label>
                      {draft.lead_magnet_enabled && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div className="quiz-editor-field">
                            <label>Resource URL</label>
                            <input style={inputStyle} placeholder="https://…"
                              value={draft.lead_magnet_url || ''}
                              onChange={e => setDraftField('lead_magnet_url', e.target.value)} />
                          </div>
                          <div className="quiz-editor-field">
                            <label>Button label</label>
                            <input style={inputStyle} placeholder="Download your free guide"
                              value={draft.lead_magnet_label || ''}
                              onChange={e => setDraftField('lead_magnet_label', e.target.value)} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default QuizzesList;
